import { useTranslation } from 'react-i18next';
import { Switch } from '@amzn/storm-ui';
import { FormRow } from '@amzn/storm-ui-column-form-kit';
import {
  WaitlistControl,
  WaitlistDetailAlert,
  WaitlistHelpTipContent,
} from './WaitlistToggleField.styles';

export interface WaitlistToggleFieldProps {
  /** Unique id for the underlying Switch. */
  id: string;
  /** Whether the waitlist is currently enabled. */
  enabled: boolean;
  /** Called with the next enabled state when the user toggles. */
  onToggle: (enabled: boolean) => void;
  /**
   * Locks the toggle on. The transition is one-way (0 -> 1) and enforced
   * by the backend, so an Art Event already waitlist-enabled can never revert.
   */
  isLocked?: boolean;
  /**
   * Shows the "default is off / optional" note and an Optional tag. Set on the
   * create form where opting in is entirely optional; omit on edit where the
   * control is a deliberate one-way advance.
   */
  showOptionalNote?: boolean;
}

/**
 * Waitlist opt-in control with an explanation of what enabling it does.
 * Shared by the create and edit Art Event forms so the copy and behaviour stay in
 * one place. Rendered as a column-form FormRow so its label and control align with
 * the other fields in the form. Enabling is a one-way transition and is gated by a
 * Weblab on the backend; the UI simply reflects the current state and lets an
 * operator opt in.
 */
const WaitlistToggleField = ({
  id,
  enabled,
  onToggle,
  isLocked = false,
  showOptionalNote = false,
}: WaitlistToggleFieldProps) => {
  const { t } = useTranslation();

  const handleChange = (toggled: boolean) => {
    // Cannot disable the waitlist once enabled; ignore attempts to turn off.
    // The Switch is rendered disabled so onChange never fires via the DOM; this is defense-in-depth.
    /* istanbul ignore next */
    if (isLocked) {
      return;
    }
    onToggle(toggled);
  };

  return (
    <FormRow
      id={`${id}-row`}
      label={t('waitlist-title')}
      // FormRow only renders the HelpTip when helpTipText is set (it also serves as
      // the trigger's accessible label); helpTipNode supplies the styled content.
      helpTipText={t('waitlist-description')}
      helpTipNode={
        <WaitlistHelpTipContent>
          {t('waitlist-description')}
        </WaitlistHelpTipContent>
      }
      optionalTag={showOptionalNote ? t('optional-tag') : undefined}
      controlComponents={[
        (componentId) => (
          <WaitlistControl key={componentId} data-testid="waitlist-field">
            <Switch
              id={componentId}
              toggled={enabled}
              disabled={isLocked}
              onChange={handleChange}
            />

            {isLocked && (
              <WaitlistDetailAlert
                variant="info"
                message={t('art-event-waitlist-locked-help')}
              />
            )}

            {!isLocked && enabled && (
              <WaitlistDetailAlert
                variant="warning"
                message={t('waitlist-one-way-warning')}
              />
            )}
          </WaitlistControl>
        ),
      ]}
    />
  );
};

export default WaitlistToggleField;
